/**
 * @sazeo/sdk — Contracts hooks
 *
 * TanStack Query wrappers for the contracts addon. All calls route through
 * the Edge Function at `/addons/contracts/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildFunctionUrl } from './functionUrl';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Contract {
  id: string;
  tenant_id: string;
  title: string;
  kind: 'employment' | 'vendor' | 'nda' | 'service' | 'other';
  status: 'draft' | 'sent' | 'signed' | 'expired' | 'terminated';
  counterparty: string | null;
  user_id: string | null;
  valid_from: string | null;
  valid_until: string | null;
  notes: string | null;
  storage_path: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContractCreateInput {
  tenant_id: string;
  title: string;
  kind?: Contract['kind'];
  status?: Contract['status'];
  counterparty?: string | null;
  user_id?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  notes?: string | null;
  storage_path?: string | null;
}

export interface ContractUpdateInput {
  title?: string;
  kind?: Contract['kind'];
  status?: Contract['status'];
  counterparty?: string | null;
  user_id?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
  notes?: string | null;
  storage_path?: string | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const contractKeys = {
  all: ['contracts'] as const,
  list: (tenantId: string) => [...contractKeys.all, 'list', tenantId] as const,
  myList: (tenantId: string) => [...contractKeys.all, 'my-list', tenantId] as const,
  detail: (id: string) => [...contractKeys.all, 'detail', id] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(buildFunctionUrl(path), {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useContracts(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: contractKeys.list(tenantId),
    queryFn: () => apiFetch<Contract[]>(supabase, 'addons/contracts/contracts'),
    enabled: Boolean(tenantId),
  });
}

export function useMyContracts(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: contractKeys.myList(tenantId),
    queryFn: () => apiFetch<Contract[]>(supabase, 'addons/contracts/contracts/mine'),
    enabled: Boolean(tenantId),
  });
}

export function useContract(supabase: SupabaseClientLike, contractId: string) {
  return useQuery({
    queryKey: contractKeys.detail(contractId),
    queryFn: () => apiFetch<Contract>(supabase, `addons/contracts/contracts/${contractId}`),
    enabled: Boolean(contractId),
  });
}

export function useCreateContract(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractCreateInput) =>
      apiFetch<Contract>(supabase, 'addons/contracts/contracts', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

export function useUpdateContract(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ContractUpdateInput }) =>
      apiFetch<Contract>(supabase, `addons/contracts/contracts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: contractKeys.detail(vars.id) });
      void qc.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

export function useDeleteContract(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/contracts/contracts/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: contractKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}
